import { EventEmitter } from 'events';
import {
  CavernsDispatcher,
  PlayerDispatcher,
  ShopDispatcher,
} from '~/flux/dispatchers';
import { awaitSocket, registerDispatchHandlers } from '~/Tools';
import { logError } from '~/logger';
import * as handlers from './handlers';
import messages from './messages';
import { Socket } from 'socket.io-client';
import Economy from 'dt-common/constants/Economy';

let playerId: string | null = null;
let _socket: Socket;
let _restock_timeout: NodeJS.Timeout | null = null;

// the stuff we serve:
let totalSaleAmount = 0;
const premiumItemList: any[] = [];
let shopInventory: any[] = [];
let lastRestock: number | null = null;

const ShopStore = Object.assign({}, EventEmitter.prototype, {
  ...messages,

  getAll() {
    return {
      totalSaleAmount,
      premiumItemList,
      shopInventory,
      lastRestock,
    };
  },
});
EventEmitter.call(ShopStore);
export default ShopStore;

const emit = ShopStore.emit.bind(ShopStore);

PlayerDispatcher.register(
  registerDispatchHandlers({
    [PlayerDispatcher.PLAYER_LOGGED_IN]: async (action: { player: any; }) => {
      setPlayerId(action.player._id);
      await handlers.refreshShopInventory({
        playerId: playerId!,
        setShopInventory,
        setLastRestock,
        setRestockTimeout,
        emit,
      });
    },
  })
);

CavernsDispatcher.register(
  registerDispatchHandlers({
    [CavernsDispatcher.RETURNED_TO_SURFACE]: async () => {
      if (!playerId) return;
      await handlers.refreshShopInventory({
        playerId,
        setShopInventory,
        setLastRestock,
        setRestockTimeout,
        emit,
      });
    },
  })
);

ShopDispatcher.register(
  registerDispatchHandlers({
    [ShopDispatcher.FETCH_SHOPKEEPER_OFFER]: fetchShopkeeperOffer,
    [ShopDispatcher.CONFIRM_ITEM_SALE]: confirmItemSale,
    [ShopDispatcher.CONFIRM_ITEM_PURCHASE]: confirmItemPurchase,
  })
);

awaitSocket(onSocketConnected);

function onSocketConnected(socket: Socket) {
  try {
    _socket = socket;

    if (!(_socket as any).has_ShopStore_listeners) {
      _socket.on('totalSaleAmount', onTotalSaleAmount);
      _socket.on('shopInventory', onShopInventory);
      _socket.on('challengeResolved', onChallengeResolved);

      (_socket as any).has_ShopStore_listeners = true;
    }
  } catch (err) {
    logError(err, {
      module: 'ShopStore',
      func: 'onSocketConnected',
    });
  }
}

function onShopInventory(data: { shopInventory: any[]; last_restock: number; }) {
  try {
    setShopInventory(data.shopInventory);
    setLastRestock(data.last_restock);
    emit(messages.SHOP_INVENTORY);

    // Handle next restock
    const next_restock = data.last_restock + Economy.SHOP_RESTOCK_INTERVAL;
    const time_until_restock = next_restock - Date.now();
    if (time_until_restock > 0) {
      const timeout = setTimeout(async () => {
        if (!playerId) return;
        await handlers.refreshShopInventory({
          playerId,
          setShopInventory,
          setLastRestock,
          setRestockTimeout,
          emit,
        });
      }, time_until_restock);
      setRestockTimeout(timeout);
    }
  } catch (err) {
    logError(err, {
      module: 'ShopStore',
      func: 'onShopInventory',
    });
  }
}

async function onChallengeResolved() {
  if (!playerId) return;
  await handlers.refreshShopInventory({
    playerId,
    setShopInventory,
    setLastRestock,
    setRestockTimeout,
    emit,
  });
}

function fetchShopkeeperOffer(action: { item_uids_to_sell: string[]; }) {
  _socket.emit('fetch_shopkeeper_offer', {
    playerId,
    item_uids: action.item_uids_to_sell,
  });
}

function onTotalSaleAmount(data: number) {
  totalSaleAmount = data;
  ShopStore.emit(messages.GOT_TOTAL_SALE_AMOUNT, totalSaleAmount);
}

function confirmItemSale() {
  _socket.emit('confirm_item_sale_to_shop', { playerId });
}

async function confirmItemPurchase(action: { itemToBuy: { uid: string; }; }) {
  if (!playerId) return;
  await handlers.purchaseShopItem({
    playerId,
    itemId: action.itemToBuy.uid,
    setShopInventory,
    setLastRestock,
    emit,
  });
}

// State setters
function setPlayerId(val: string) {
  playerId = val;
}

function setShopInventory(val: any[]) {
  shopInventory = val;
}

function setLastRestock(val: number) {
  lastRestock = val;
}

function setRestockTimeout(timeout: NodeJS.Timeout | null) {
  if (_restock_timeout) {
    clearTimeout(_restock_timeout);
  }
  _restock_timeout = timeout;
}
